import React, {useEffect, useState} from 'react';
import {
    deleteUser,
    fetchUsers,
    inviteUser,
    reInviteUser,
    updateUserPassword, updateUserPublishMode,
    updateUserRole
} from "../services/apiService"; // Assuming updateUserRole is in apiService
import {useAuth} from "../utils/auth.context";
import '../css/UsersPage.css';
import Translate, {t} from "../components/Translate";
import {getCurrentUser} from "../services/authService";
import {toast} from "react-toastify";
import DeleteIcon from "../assets/img/delete.png";
import EditIcon from "../assets/img/edit.png";
import ResendIcon from "../assets/img/import.png";
import Tooltip from "../components/Tooltip";

const currentUser = getCurrentUser() ? getCurrentUser() : {language: 'en'};

const UsersPage = () => {
    const {user} = useAuth();
    const [message, setMessage] = useState('');
    const [users, setUsers] = useState([]);
    const [username, setUsername] = useState('');
    const [role, setRole] = useState('ADMIN');
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [selectedUser, setSelectedUser] = useState(null);
    const [newPassword, setNewPassword] = useState('');


    useEffect(() => {
        fetchUsersList();
    }, []);


    const handleChangePassword = (user) => {
        setSelectedUser(user);
        setShowPasswordModal(true);
    };

    const handlePasswordChangeSubmit = async (e) => {
        e.preventDefault();
        try {
            await updateUserPassword(selectedUser.username, newPassword);
            toast.success(t(currentUser.language, 'passwordUpdated'), {position: "top-right", theme: "colored"});
            setShowPasswordModal(false);
        } catch (error) {
            console.log(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const fetchUsersList = async () => {
        try {
            const data = await fetchUsers();
            setUsers(data.filter(d => d.username !== user.data._username));
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleDeleteUser = async (user) => {
        try {
            await deleteUser(user.id);
            toast.info(t(currentUser.language, 'deleted'), {position: "top-right", theme: "colored"});
            setUsers(users.filter(u => u.id !== user.id));
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'userAlreadyAssigned'), {position: "top-right", theme: "colored"});
        }
    };

    const handleInvite = async (e) => {
        e.preventDefault();
        try {
            await inviteUser(username, role);
            toast.success(t(currentUser.language, 'success'), {position: "top-right", theme: "colored"});
            fetchUsersList();
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleResendInvitation = async (user) => {
        try {
            await reInviteUser(user.username, user.role);
            toast.success(t(currentUser.language, 'success'), {position: "top-right", theme: "colored"});
        } catch (error) {
            console.log(error)
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleRoleChange = async (username, newRole) => {
        try {
            await updateUserRole(username, newRole);
            setUsers(users.map(u => u.username === username ? {...u, role: newRole} : u));
            toast.success(t(currentUser.language, 'roleUpdated'), {position: "top-right", theme: "colored"});
        } catch (error) {
            console.log(error);
            toast.error(t(currentUser.language, 'error'), {position: "top-right", theme: "colored"});
        }
    };

    const handleTogglePublishMode = async (user) => {
        const newPublishMode = !user.isPublishModeActive;
        try {
            await updateUserPublishMode(user.username, newPublishMode);
            setUsers(users.map(u => u.username === user.username ? { ...u, isPublishModeActive: newPublishMode } : u));
            toast.success(t(currentUser.language, 'publishModeUpdated'), { position: "top-right", theme: "colored" });
        } catch (error) {
            console.error(error);
            toast.error(t(currentUser.language, 'error'), { position: "top-right", theme: "colored" });
        }
    };

    return (
        <div className="users-page">
            <div className="invite-section">
                <h3 className="invite-title"><Translate language={currentUser.language} text={'inviteUser'}/></h3>
                <form onSubmit={handleInvite} className="invite-form">
                    <input
                        type="email"
                        placeholder="Email"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        className="invite-input"
                        required
                    />
                    <div className="custom-select-wrapper" onClick={() => setDropdownOpen(!dropdownOpen)}>
                        <div className="custom-select">
                            <div className="select-placeholder">{role}</div>
                            <div className="select-arrow">▼</div>
                        </div>
                        {dropdownOpen && (
                            <div className="select-options">
                                <div className="select-option" onClick={() => setRole('ADMIN')}>ADMIN</div>
                                <div className="select-option" onClick={() => setRole('MANAGER')}>MANAGER</div>
                                <div className="select-option" onClick={() => setRole('EDITOR')}>EDITOR</div>
                            </div>
                        )}
                    </div>
                    <button type="submit" className="invite-button"><Translate language={currentUser.language}
                                                                               text={'sendInvitation'}/></button>
                </form>
                {message && <p className="invite-message">{message}</p>}
            </div>

            <div className="user-table-container">
                <h3 className="user-table-title"><Translate language={currentUser.language} text={'usersList'}/></h3>
                <table className="user-table">
                    <thead>
                    <tr>
                        <th><span className="user-table-header-label"><Translate language={currentUser.language} text={'username'}/></span></th>
                        <th className="user-role-column"><span className="user-table-header-label"><Translate language={currentUser.language} text={'role'}/></span></th>
                        <th className="user-enabled-column"><span className="user-table-header-label"><Translate language={currentUser.language} text={'enabled'}/></span></th>
                        <th className="user-publish-column"><span className="user-table-header-label"><Translate language={currentUser.language} text={'publishMode'}/></span></th>
                        <th className="user-action-column"><span className="user-table-header-label"><Translate language={currentUser.language} text={'action'}/></span></th>
                    </tr>
                    </thead>
                    <tbody>
                    {users.map((user, index) => (
                        <tr key={index} className="user-row">
                            <td>
                                <span className="user-name-cell" title={user.username}>{user.username}</span>
                            </td>
                            <td className="user-role-column">
                                <select
                                    value={user.role}
                                    onChange={(e) => handleRoleChange(user.username, e.target.value)}
                                    className="role-select"
                                >
                                    <option value="ADMIN">ADMIN</option>
                                    <option value="MANAGER">MANAGER</option>
                                    <option value="EDITOR">EDITOR</option>
                                </select>
                            </td>
                            <td className="user-enabled-column">
                                <span className={`user-status-chip ${user.isEnabled ? 'enabled' : 'disabled'}`}>
                                    {user.isEnabled ? t(currentUser.language, "yes") : t(currentUser.language, "no")}
                                </span>
                            </td>
                            <td className="user-publish-column">
                                <input
                                    type="checkbox"
                                    checked={user.isPublishModeActive}
                                    onChange={() => handleTogglePublishMode(user)}
                                    className="publish-mode-checkbox"
                                />
                            </td>
                            <td className="user-action-column">
                                <div className="user-actions">
                                    {!user.isEnabled && (
                                        <Tooltip text={t(currentUser.language, 'resend')}>
                                            <img
                                                className="user-action-icon"
                                                onClick={() => handleResendInvitation(user)}
                                                width={24}
                                                height={24}
                                                src={ResendIcon}
                                                alt={t(currentUser.language, 'resend')}
                                            />
                                        </Tooltip>
                                    )}
                                    <Tooltip text={t(currentUser.language, 'delete')}>
                                        <img
                                            className="user-action-icon"
                                            onClick={() => handleDeleteUser(user)}
                                            width={24}
                                            height={24}
                                            src={DeleteIcon}
                                            alt={t(currentUser.language, 'delete')}
                                        />
                                    </Tooltip>
                                    <Tooltip text={t(currentUser.language, 'changePassword')}>
                                        <img
                                            className="user-action-icon"
                                            onClick={() => handleChangePassword(user)}
                                            width={24}
                                            height={24}
                                            src={EditIcon}
                                            alt={t(currentUser.language, 'changePassword')}
                                        />
                                    </Tooltip>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>


            {showPasswordModal && (
                <div className="password-modal">
                    <div className="password-modal-content">
                        <h3><Translate language={currentUser.language}
                                       text={'changePassword'}/> : {selectedUser.username}</h3>
                        <form onSubmit={handlePasswordChangeSubmit}>
                            <input
                                type="password"
                                placeholder={t(currentUser.language, 'newPassword')}
                                value={newPassword}
                                onChange={(e) => setNewPassword(e.target.value)}
                                required
                                className="password-input"
                            />
                            <div className={'new-password-modal-buttons-div'}>
                                <button onClick={() => {
                                    setNewPassword("");
                                    setShowPasswordModal(false);
                                }} className="rbutton cancel">
                                    <Translate language={currentUser.language} text={'cancel'}/>
                                </button>
                                <button type="submit" className="rbutton">
                                    <Translate language={currentUser.language} text={'submit'}/>
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UsersPage;
